import { call, put, takeLatest } from 'redux-saga/effects'
import type { PayloadAction } from '@reduxjs/toolkit'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/lib/firebase'
import type { ApiKey } from './apiKeysSlice'
import {
    fetchApiKeysSaga,
    createApiKeySaga,
    deleteApiKeySaga,
    setKeys,
    setNewKey,
    addKey,
    removeKey,
    setLoading,
    setError,
} from './apiKeysSlice'
import { addLoadingKey, removeLoadingKey } from '../ui/uiSlice'
import { AnalyticsEvent, trackEvent } from '@/lib/analytics'

function* fetchApiKeysWorker(action: PayloadAction<string>): Generator<any, void, any> {
    yield put(setLoading(true))
    try {
        const fn = httpsCallable<{ workspaceId: string }, ApiKey[]>(functions, 'listApiKeys')
        const result: { data: ApiKey[] } = yield call(() => fn({ workspaceId: action.payload }))
        yield put(setKeys(result.data))
    } catch (error: any) {
        yield put(setError(error.message))
    } finally {
        yield put(setLoading(false))
    }
}

function* createApiKeyWorker(action: PayloadAction<{ workspaceId: string; name: string }>): Generator<any, void, any> {
    yield put(addLoadingKey('create-api-key'))
    try {
        const fn = httpsCallable<{ workspaceId: string; name: string }, ApiKey & { key: string }>(functions, 'createApiKey')
        const result: { data: ApiKey & { key: string } } = yield call(() => fn(action.payload))
        const { key, ...keyRecord } = result.data
        yield put(addKey(keyRecord))
        yield put(setNewKey(key))
        // Activation: external tooling hooked up (fired only after the callable
        // succeeded; the key itself and its name never go to analytics).
        trackEvent(AnalyticsEvent.ApiKeyCreated, { workspace_id: action.payload.workspaceId })
    } catch (error: any) {
        yield put(setError(error.message))
    } finally {
        yield put(removeLoadingKey('create-api-key'))
    }
}

function* deleteApiKeyWorker(action: PayloadAction<{ id: string; workspaceId: string }>): Generator<any, void, any> {
    yield put(addLoadingKey(`delete-api-key-${action.payload.id}`))
    try {
        const fn = httpsCallable<{ workspaceId: string; keyId: string }, { success: boolean }>(functions, 'deleteApiKey')
        yield call(() => fn({ workspaceId: action.payload.workspaceId, keyId: action.payload.id }))
        yield put(removeKey(action.payload.id))
    } catch (error: any) {
        yield put(setError(error.message))
    } finally {
        yield put(removeLoadingKey(`delete-api-key-${action.payload.id}`))
    }
}

export function* watchApiKeysSaga() {
    yield takeLatest(fetchApiKeysSaga.type, fetchApiKeysWorker)
    yield takeLatest(createApiKeySaga.type, createApiKeyWorker)
    yield takeLatest(deleteApiKeySaga.type, deleteApiKeyWorker)
}
